import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/axios";
import "../../assets/css/Deals/DealActivityTimeline.css";
import DealDocumentPreviewModal from "./DealDocumentPreviewModal";
import {
  FaArrowRight,
  FaUserCheck,
  FaPhoneAlt,
  FaMoneyBillWave,
  FaSyncAlt,
  FaPlusCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaHistory,
  FaCalendarAlt,
  FaClock,
  FaSortAmountDown,
  FaSortAmountUp,
  FaSpinner,
  FaFilter,
  FaPaperclip,
  FaFileDownload,
  FaEye,
} from "react-icons/fa";

export default function DealActivityTimeline({
  dealId,
  refreshKey = 0,
}) {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const limit = 30;

  const handleTimelineDocDownload = async (documentId, filename) => {
    try {
      const res = await api.get(`/deal/documents/${documentId}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = filename || `document_${documentId}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Timeline document download failed:", err);
    }
  };

  const fetchTimeline = useCallback(
    async (currentOffset = 0, append = false) => {
      if (!dealId) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const catQuery = category && category !== "all" ? `&category=${category}` : "";
        const url = `/deal/${dealId}/timeline?sort_order=${sortOrder}&limit=${limit}&offset=${currentOffset}${catQuery}`;
        const res = await api.get(url);

        const data = res.data || {};
        const incomingEvents = data.events || [];

        if (append) {
          setEvents((prev) => [...prev, ...incomingEvents]);
        } else {
          setEvents(incomingEvents);
        }

        setSummary(data.summary || null);
        setHasMore(data.has_more || false);
        setOffset(currentOffset);
      } catch (err) {
        console.error("Failed to load deal timeline", err);
        setError("Unable to load activity timeline. Please try again.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [dealId, category, sortOrder, limit]
  );

  useEffect(() => {
    fetchTimeline(0, false);
  }, [fetchTimeline, refreshKey]);

  const handleCategoryChange = (cat) => {
    if (category === cat) return;
    setCategory(cat);
    setOffset(0);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setOffset(0);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      const nextOffset = offset + limit;
      fetchTimeline(nextOffset, true);
    }
  };

  const renderIcon = (cat, eventType) => {
    switch (cat) {
      case "STAGE_TRANSITION":
        return <FaArrowRight />;
      case "ASSIGNMENT_CHANGE":
        return <FaUserCheck />;
      case "FOLLOWUP_INTERACTION":
        return <FaPhoneAlt />;
      case "FINANCIAL_EXPENSE":
        return <FaMoneyBillWave />;
      case "CONTRACT_RENEWAL":
        return <FaSyncAlt />;
      case "DOCUMENT_ATTACHMENT":
        return <FaPaperclip />;
      case "DEAL_LIFECYCLE":
        if (eventType === "DEAL_WON") return <FaCheckCircle />;
        if (eventType === "DEAL_LOST") return <FaTimesCircle />;
        return <FaPlusCircle />;
      default:
        return <FaHistory />;
    }
  };

  const formatTimestamp = (isoStr) => {
    if (!isoStr) return "-";
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoStr;
    }
  };

  const categories = [
    { key: "all", label: "All Activity", count: summary?.total_events },
    {
      key: "DOCUMENT_ATTACHMENT",
      label: "Documents",
      count: summary?.documents_count,
    },
    {
      key: "ASSIGNMENT_CHANGE",
      label: "Ownership",
      count: summary?.assignment_changes_count,
    },
    {
      key: "FOLLOWUP_INTERACTION",
      label: "Follow-ups",
      count: summary?.followup_interactions_count,
    },
    {
      key: "FINANCIAL_EXPENSE",
      label: "Expenses",
      count: summary?.expenses_count,
    },
    {
      key: "CONTRACT_RENEWAL",
      label: "Renewals",
      count: summary?.renewals_count,
    },
  ];

  return (
    <div className="deal-timeline-container" data-testid="deal-activity-timeline">
      {/* Controls & Filter Bar */}
      <div className="deal-timeline-controls">
        <div className="deal-timeline-filter-bar">
          {categories.map((c) => (
            <button
              key={c.key}
              className={`timeline-filter-btn ${category === c.key ? "active" : ""}`}
              onClick={() => handleCategoryChange(c.key)}
              type="button"
            >
              <span>{c.label}</span>
              {c.count !== undefined && c.count > 0 && (
                <span className="timeline-count-badge">{c.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="deal-timeline-actions-row">
          <span>
            Showing <strong>{events.length}</strong> of{" "}
            <strong>{summary?.total_events || events.length}</strong> events
          </span>

          <button
            className="timeline-sort-btn"
            onClick={toggleSortOrder}
            type="button"
            title="Toggle chronological sort order"
          >
            {sortOrder === "desc" ? (
              <>
                <FaSortAmountDown />
                <span>Newest First</span>
              </>
            ) : (
              <>
                <FaSortAmountUp />
                <span>Oldest First</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="deal-timeline-loading">
          <FaSpinner className="spinner fa-spin" />
          <p>Collating deal activity timeline...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="deal-timeline-error">
          <p>{error}</p>
          <button onClick={() => fetchTimeline(0, false)} type="button">
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && events.length === 0 && (
        <div className="deal-timeline-empty">
          <FaHistory className="empty-icon" />
          <h4>No Activity Recorded</h4>
          <p>
            {category !== "all"
              ? "No events found matching the selected category filter."
              : "No actions or transitions have been logged for this deal yet."}
          </p>
        </div>
      )}

      {/* Timeline Stream */}
      {!loading && !error && events.length > 0 && (
        <div className="deal-timeline-stream">
          {events.map((event) => (
            <div key={event.id} className="deal-timeline-item">
              {/* Node Icon */}
              <div className={`deal-timeline-node ${event.badge_variant || "info"}`}>
                {renderIcon(event.category, event.event_type)}
              </div>

              {/* Card Body */}
              <div className="deal-timeline-card">
                <div className="timeline-card-header">
                  <div className="timeline-card-title-group">
                    <h5 className="timeline-card-title">{event.title}</h5>
                    <span className={`timeline-category-tag ${event.category}`}>
                      {event.category.replace("_", " ")}
                    </span>
                  </div>

                  <span className="timeline-timestamp">
                    {formatTimestamp(event.timestamp)}
                  </span>
                </div>

                {/* Follow-Up Specific Layout */}
                {event.category === "FOLLOWUP_INTERACTION" ? (
                  <div className="timeline-followup-details">
                    {/* Follow-Up Reason Box */}
                    {event.metadata?.remarks ? (
                      <div className="timeline-followup-reason-box">
                        <div className="timeline-reason-header">
                          <span className="timeline-reason-badge">Reason / Notes</span>
                        </div>
                        <p className="timeline-reason-text">{event.metadata.remarks}</p>
                      </div>
                    ) : event.description ? (
                      <p className="timeline-card-description">{event.description}</p>
                    ) : null}

                    {/* Metadata Items: Next Scheduled Follow-up & Stage */}
                    <div className="timeline-metadata-box" style={{ marginTop: 8 }}>
                      {event.metadata?.new_stage && (
                        <div className="timeline-meta-item">
                          <span className="timeline-meta-label">Stage:</span>
                          <span className="timeline-meta-val stage-badge">{event.metadata.new_stage}</span>
                        </div>
                      )}
                      {event.metadata?.scheduled_date && (
                        <div className="timeline-meta-item">
                          <FaCalendarAlt size={12} color="#0284c7" />
                          <span className="timeline-meta-label">Next Follow-Up:</span>
                          <span className="timeline-meta-val">
                            {event.metadata.scheduled_date} {event.metadata.scheduled_time || ""}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Attached Document in Follow-Up */}
                    {event.metadata?.document_id && (
                      <div className="timeline-attached-doc-card">
                        <div className="timeline-doc-card-left">
                          <div className="timeline-doc-icon-wrap">
                            <FaPaperclip size={14} />
                          </div>
                          <div className="timeline-doc-meta">
                            <div className="timeline-doc-title-row">
                              <span className={`timeline-doc-type-badge ${event.metadata.doc_type?.toLowerCase() || "other"}`}>
                                {event.metadata.doc_type || "Document"}
                              </span>
                              <span className="timeline-doc-filename" title={event.metadata.original_filename}>
                                {event.metadata.original_filename || "Attached Document"}
                              </span>
                            </div>
                            <div className="timeline-doc-sub-row">
                              {event.metadata.doc_number && (
                                <span className="timeline-doc-number">#{event.metadata.doc_number}</span>
                              )}
                              {event.metadata.amount !== undefined && event.metadata.amount !== null && event.metadata.amount > 0 && (
                                <span className="timeline-doc-amount">₹{Number(event.metadata.amount).toLocaleString("en-IN")}</span>
                              )}
                              {event.metadata.file_size_formatted && (
                                <span className="timeline-doc-size">{event.metadata.file_size_formatted}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="timeline-doc-card-actions">
                          <button
                            type="button"
                            className="timeline-doc-action-btn preview"
                            onClick={() => setPreviewDoc({
                              id: event.metadata.document_id,
                              title: event.metadata.original_filename,
                              original_filename: event.metadata.original_filename,
                              doc_type: event.metadata.doc_type,
                              mime_type: "application/pdf",
                              download_url: event.metadata.download_url || `/deal/documents/${event.metadata.document_id}/download`,
                              preview_url: event.metadata.preview_url || `/deal/documents/${event.metadata.document_id}/preview`,
                            })}
                            title="Preview Document"
                          >
                            <FaEye size={12} />
                            <span>Preview</span>
                          </button>
                          <button
                            type="button"
                            className="timeline-doc-action-btn download"
                            onClick={() =>
                              handleTimelineDocDownload(
                                event.metadata.document_id,
                                event.metadata.original_filename
                              )
                            }
                            title="Download Document"
                          >
                            <FaFileDownload size={12} />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {event.description && (
                      <p className="timeline-card-description">{event.description}</p>
                    )}

                    {/* Specific Metadata Details */}
                    {event.metadata && (
                      <>
                        {/* Stage Transition Metadata */}
                        {event.category === "STAGE_TRANSITION" &&
                          event.metadata.old_stage &&
                          event.metadata.new_stage && (
                            <div className="timeline-metadata-box">
                              <div className="timeline-meta-item">
                                <span className="timeline-meta-label">From:</span>
                                <span className="timeline-meta-val stage-badge">
                                  {event.metadata.old_stage}
                                </span>
                              </div>
                              <FaArrowRight size={10} color="#94a3b8" />
                              <div className="timeline-meta-item">
                                <span className="timeline-meta-label">To:</span>
                                <span className="timeline-meta-val stage-badge">
                                  {event.metadata.new_stage}
                                </span>
                              </div>
                            </div>
                          )}

                        {/* Assignment Metadata */}
                        {event.category === "ASSIGNMENT_CHANGE" &&
                          (event.metadata.duration_days !== undefined ||
                            event.metadata.reassignment_reason) && (
                            <div className="timeline-metadata-box">
                              {event.metadata.duration_days !== undefined && (
                                <div className="timeline-meta-item">
                                  <FaClock size={12} color="#64748b" />
                                  <span className="timeline-meta-label">Tenure:</span>
                                  <span className="timeline-meta-val">
                                    {event.metadata.duration_days} days
                                  </span>
                                </div>
                              )}
                              {event.metadata.reassignment_reason && (
                                <div className="timeline-meta-item">
                                  <span className="timeline-meta-label">Reason:</span>
                                  <span className="timeline-meta-val">
                                    {event.metadata.reassignment_reason}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                        {/* Financial Expense Metadata */}
                        {event.category === "FINANCIAL_EXPENSE" &&
                          event.metadata.amount !== undefined && (
                            <div className="timeline-metadata-box">
                              <div className="timeline-meta-item">
                                <span className="timeline-meta-label">Amount:</span>
                                <span className="timeline-meta-val currency">
                                  ₹{event.metadata.amount.toLocaleString()}
                                </span>
                              </div>
                              {event.metadata.expense_type && (
                                <div className="timeline-meta-item">
                                  <span className="timeline-meta-label">Type:</span>
                                  <span className="timeline-meta-val">
                                    {event.metadata.expense_type}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                        {/* Renewal Metadata */}
                        {event.category === "CONTRACT_RENEWAL" && (
                          <div className="timeline-metadata-box">
                            {event.metadata.amount !== undefined && (
                              <div className="timeline-meta-item">
                                <span className="timeline-meta-label">Renewal Amount:</span>
                                <span className="timeline-meta-val currency">
                                  ₹{event.metadata.amount.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {event.metadata.next_renewal_date && (
                              <div className="timeline-meta-item">
                                <FaCalendarAlt size={12} color="#64748b" />
                                <span className="timeline-meta-label">Next Renewal:</span>
                                <span className="timeline-meta-val">
                                  {event.metadata.next_renewal_date}
                                </span>
                              </div>
                            )}
                            {event.metadata.remaining_days !== undefined && (
                              <div className="timeline-meta-item">
                                <span className="timeline-meta-label">Days Left:</span>
                                <span className="timeline-meta-val">
                                  {event.metadata.remaining_days} days
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Standalone Document Attachment Metadata */}
                        {event.category === "DOCUMENT_ATTACHMENT" && (
                          <div className="timeline-metadata-box" style={{ alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                              {event.metadata.doc_number && (
                                <div className="timeline-meta-item">
                                  <span className="timeline-meta-label">Doc #:</span>
                                  <span className="timeline-meta-val">{event.metadata.doc_number}</span>
                                </div>
                              )}
                              {event.metadata.amount !== undefined && event.metadata.amount !== null && (
                                <div className="timeline-meta-item">
                                  <span className="timeline-meta-label">Value:</span>
                                  <span className="timeline-meta-val currency">
                                    ₹{Number(event.metadata.amount).toLocaleString("en-IN")}
                                  </span>
                                </div>
                              )}
                              {event.metadata.file_size_formatted && (
                                <div className="timeline-meta-item">
                                  <span className="timeline-meta-label">Size:</span>
                                  <span className="timeline-meta-val">{event.metadata.file_size_formatted}</span>
                                </div>
                              )}
                            </div>
                            {event.metadata.document_id && (
                              <div style={{ display: "flex", gap: "6px", marginLeft: "auto" }}>
                                <button
                                  type="button"
                                  className="timeline-sort-btn"
                                  style={{ padding: "4px 8px", fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                                  onClick={() => setPreviewDoc({
                                    id: event.metadata.document_id,
                                    title: event.metadata.original_filename,
                                    original_filename: event.metadata.original_filename,
                                    doc_type: event.metadata.doc_type,
                                    mime_type: "application/pdf",
                                    download_url: event.metadata.download_url || `/deal/documents/${event.metadata.document_id}/download`,
                                    preview_url: event.metadata.preview_url || `/deal/documents/${event.metadata.document_id}/preview`,
                                  })}
                                >
                                  <FaEye size={12} />
                                  <span>Preview</span>
                                </button>
                                <button
                                  type="button"
                                  className="timeline-sort-btn"
                                  style={{ padding: "4px 8px", fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                                  onClick={() =>
                                    handleTimelineDocDownload(
                                      event.metadata.document_id,
                                      event.metadata.original_filename
                                    )
                                  }
                                >
                                  <FaFileDownload size={12} />
                                  <span>Download</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Footer with Actor info */}
                {event.actor && (
                  <div className="timeline-card-footer">
                    <div className="timeline-actor-chip">
                      <div className="timeline-actor-avatar">
                        {(event.actor.name || "U")[0].toUpperCase()}
                      </div>
                      <span className="timeline-actor-name">
                        {event.actor.name}
                      </span>
                    </div>

                    {event.actor.role && (
                      <span className="timeline-actor-role">
                        {event.actor.role.replace("_", " ")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="timeline-load-more">
              <button
                className="timeline-load-more-btn"
                onClick={handleLoadMore}
                disabled={loadingMore}
                type="button"
              >
                {loadingMore ? (
                  <>
                    <FaSpinner className="fa-spin" style={{ marginRight: 6 }} />
                    Loading more events...
                  </>
                ) : (
                  "Load More Activity"
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* In-app Document Preview Modal */}
      {previewDoc && (
        <DealDocumentPreviewModal
          isOpen={Boolean(previewDoc)}
          onClose={() => setPreviewDoc(null)}
          document={previewDoc}
        />
      )}
    </div>
  );
}

